import { isString } from 'lodash';
import React from 'react';
import tinycolor from 'tinycolor2';
import { ILinearGradient, IVectorEffect } from '@/version-types';
import { parseStrokeDasharray } from '../file-kit';
import BaseSvg from './base-svg';
import { LinearGradient } from './linear-gradient';
import { getBlurOrReflectionParams, hasReflection, hasShadow } from './utils';

/**
 * 绘制svg 矩形/椭圆
 */
export const DrawSvg: React.FC<{
    id: string;
    /** 类型， rect, ellipse, wavy */
    type: string;
    /** 圆角 */
    radius: number;
    /** 宽 */
    width: number;
    /** 高 */
    height: number;
    /** 填充色 */
    fill: ILinearGradient | string;
    /** 描边色 */
    stroke: ILinearGradient | string;
    /** 描边宽 */
    strokeWidth: number;
    /** 描边样式 */
    strokeDasharray: string;
    /** 描边样式-类型 */
    strokeLinecap: string;
    /** 额外定义属性 */
    showDrawId?: boolean;
    /** 波浪线原始宽度 */
    naturalWidth?: number;
    /** 波浪线, 基础路径 */
    pathD?: string;
    /** 波浪线, 重复的路径 */
    repeatD?: string;
    /** 模糊 */
    blur?: number;
    /** 特效 */
    effect?: IVectorEffect;
    /** 描边是否隐藏 */
    strokeHidden?: boolean;
}> = React.memo(
    ({
        showDrawId = false,
        id,
        type,
        radius,
        width,
        height,
        fill,
        stroke,
        strokeWidth,
        strokeDasharray,
        strokeLinecap,
        naturalWidth = 0,
        pathD = '',
        repeatD = '',
        strokeHidden = false,
        blur,
        effect,
    }) => {
        const shadow = effect?.shadow[0];
        const reflection = effect?.reflection[0];
        const isFillGradient = fill ? !isString(fill) : false;
        const isStrokeGradient = stroke ? !isString(stroke) : false;
        const drawId = `${showDrawId ? '' : '_icon'}`;
        const fillId = `FILL_${id}${drawId}`;
        const strokeId = `STROKE_${id}${drawId}`;
        const svgStyle: React.CSSProperties = {
            strokeWidth: 0,
        };
        if (fill) {
            svgStyle.fill = isFillGradient ? `url(#${fillId})` : `${fill}`;
        }
        //没有隐藏并且有值
        if (!strokeHidden && stroke) {
            if (stroke) {
                svgStyle.stroke = isStrokeGradient ? `url(#${strokeId})` : `${stroke}`;
            }
            if (strokeDasharray) {
                svgStyle.strokeDasharray = parseStrokeDasharray(strokeDasharray, strokeWidth) as string;
            }
            if (strokeLinecap) {
                svgStyle.strokeLinecap = strokeLinecap as any;
            }
            if (strokeWidth) {
                svgStyle.strokeWidth = strokeWidth;
            }
        }
        //隐藏描边
        if (strokeHidden) strokeWidth = 0;

        const repeatPathD: string[] = [];
        if (type === 'wavy') {
            const count = Math.ceil(width / naturalWidth) || 0;
            for (let i = 0; i < count; i++) {
                repeatPathD.push(repeatD);
            }
        }

        const blurTag = (blur: any, name: string) => {
            if (!blur) return null;
            const type: any = name === 'blur' ? 'reflection' : name;
            const { XY, WH } = getBlurOrReflectionParams(blur, type);

            // 提前处理颜色和阴影逻辑
            let color = '';
            if (name === 'blur') {
                color = typeof fill === 'string' ? fill : fill.colors[0].color;
            } else {
                color = shadow?.color || '';
            }
            // 解析颜色为 RGB
            const { r, g, b } = tinycolor(color).toRgb();
            return (
                <filter
                    id={`${name}-filter_${id}`}
                    filterUnits="objectBoundingBox"
                    x={XY}
                    y={XY}
                    width={WH}
                    height={WH}
                >
                    <feGaussianBlur in={name === 'reflection' ? 'SourceGraphic' : 'SourceAlpha'} stdDeviation={blur} />
                    {name !== 'reflection' && (
                        <feComponentTransfer colorInterpolationFilters="sRGB">
                            <feFuncR type="linear" slope="0" intercept={r / 255}></feFuncR>
                            <feFuncG type="linear" slope="0" intercept={g / 255}></feFuncG>
                            <feFuncB type="linear" slope="0" intercept={b / 255}></feFuncB>
                            <feFuncA type="linear" slope="1" intercept="0"></feFuncA>
                        </feComponentTransfer>
                    )}
                </filter>
            );
        };

        // 由于mask不包含strokewidth，所以需要计算最大的描边宽度，以此来计算mask的高度
        const computedMaskHeight = () => {
            const maxStrokeWidth = Math.ceil(Math.min(width, height) / 2);
            const maxHeight = 4.5;
            const minHeight = 3;
            const factor = (maxHeight - minHeight) / (maxStrokeWidth - 30);
            return Math.max(factor * (strokeWidth - 30) + minHeight, minHeight);
        };

        return (
            <svg
                {...(showDrawId && { 'data-draw-id': `svg-${id}` })}
                viewBox={`0 0 ${width} ${height}`}
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                style={{ display: 'block', overflow: effect || blur ? 'visible' : 'hidden' }}
            >
                {isFillGradient || isStrokeGradient || blur || shadow || reflection ? (
                    <defs>
                        {isFillGradient ? (
                            <LinearGradient id={fillId} width={width} height={height} linearGradient={fill as any} />
                        ) : null}
                        {isStrokeGradient ? (
                            <LinearGradient
                                id={strokeId}
                                width={width}
                                height={height}
                                linearGradient={stroke as any}
                            />
                        ) : null}

                        {/* 模糊 */}
                        {blurTag(blur, 'blur')}

                        {/* 投影 */}
                        {shadow && hasShadow(effect) ? blurTag(shadow.blur, 'shadow') : null}

                        {/* 倒影 */}
                        {reflection && hasReflection(effect) ? (
                            <>
                                {blurTag(reflection.blur, 'reflection')}
                                <linearGradient
                                    id={`fade_${id}`}
                                    gradientUnits="objectBoundingBox"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                    gradientTransform="scale(0.33)"
                                >
                                    <stop offset={`${100 - reflection.size}%`} stopColor="rgb(0,0,0)" stopOpacity="0" />
                                    <stop offset="100%" stopColor="rgb(255,255,255)" stopOpacity="0.8" />
                                </linearGradient>
                                {/* 定义遮罩 */}
                                <mask
                                    id={`mask_${id}`}
                                    maskContentUnits="objectBoundingBox"
                                    x="-1"
                                    y="0"
                                    width="3"
                                    height="3"
                                >
                                    <rect
                                        x="-1"
                                        y="0"
                                        width="3"
                                        height={computedMaskHeight()}
                                        fill={`url(#fade_${id})`}
                                    />
                                </mask>
                            </>
                        ) : null}
                    </defs>
                ) : null}
                {type === 'wavy' ? (
                    <path
                        d={`${pathD}${repeatPathD.join('')}`}
                        style={{ ...svgStyle, transform: `translateY(${strokeWidth / 2 || 0}px)` }}
                    />
                ) : (
                    <>
                        {type === 'rect' ? (
                            <BaseSvg
                                x={strokeWidth / 2}
                                y={strokeWidth / 2}
                                rx={radius}
                                ry={radius}
                                width={width - strokeWidth >= 1 ? width - strokeWidth : 1}
                                height={height - strokeWidth >= 1 ? height - strokeWidth : 1}
                                naturalHeight={height}
                                naturalWidth={width}
                                style={svgStyle}
                                // strokeLinejoin="round"
                                effect={effect}
                                tag="rect"
                                id={id}
                                blur={blur}
                            ></BaseSvg>
                        ) : (
                            <BaseSvg
                                cx={width / 2}
                                cy={height / 2}
                                rx={width / 2 - strokeWidth / 2 >= 1 ? width / 2 - strokeWidth / 2 : 1}
                                ry={height / 2 - strokeWidth / 2 >= 1 ? height / 2 - strokeWidth / 2 : 1}
                                naturalHeight={height}
                                naturalWidth={width}
                                style={svgStyle}
                                effect={effect}
                                tag="ellipse"
                                id={id}
                                blur={blur}
                            ></BaseSvg>
                        )}
                    </>
                )}
            </svg>
        );
    },
);
